// Timeline-Länge als eine Entscheidung (GitHub #1, Punkt 6; Spec
// docs/superpowers/specs/2026-09-14-flex-timeline-multilingual-design.md).
//
// Standard-Projekte: exakt die bisherige Kette aus getAssetTimelineDuration,
// Overtime bis 2×, Export bis zum letzten Voice-Tail.
//
// Flex (Multi-Lingual, `config._simpleMode`): keine feste Länge. Anzeige
// = letztes Clip-Ende + 1 s Luft (mind. 10 s), Zieh-Zone = max(60 s,
// 2 × Summe aller Chip-Dauern), Export = exakt letztes Clip-Ende.
// Die Zieh-Zone hängt bewusst an der Summe, nicht am letzten Ende: ein
// Deckel am letzten Ende wächst nach jedem Drag mit (Ratsche → unendlich),
// die Summe ändert sich beim Verschieben nicht (Philip, 2026-09-14). Video-,
// Custom- und Bett-Längen kommen im Flex-Flow nicht vor und werden
// ignoriert; die Admin-`targetDuration` ist dort nur ein Richtwert
// (Warn-Pill + Limit-Linie), nie die Länge.

import type { Block, MasterClip } from '@shared/types';
import { getAssetTimelineDuration } from './timeline';

export const FLEX_MIN_DISPLAY_SEC = 10;
export const FLEX_TAIL_SEC = 1;
export const FLEX_MIN_DRAG_SEC = 60;

export interface TimelineLength {
  /** Anzeige-Länge: Lineal, Sticky-Fit, End-Marker (Standard). */
  boundary: number;
  /** Bis hierhin darf ein Chip-Ende gezogen werden. */
  dragCap: number;
  /** Länge des gerenderten Exports. */
  exportLength: number;
  flex: boolean;
}

export interface TimelineLengthInput {
  flex: boolean;
  assetIdx: number;
  videoDurations: Record<number, number>;
  targetDuration: number | null | undefined;
  customDurations: Record<number, number>;
  bedDuration?: number | null;
  /** Ende des letzten Voice-Clips (0 ohne Clips). */
  lastClipEnd: number;
  /** Summe aller Chip-Dauern — nur für die Flex-Zieh-Zone relevant.
   *  Aufrufer, die `dragCap` nicht nutzen (Export, VersionsBar), lassen es weg. */
  contentDuration?: number;
}

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}

export function resolveTimelineLength(input: TimelineLengthInput): TimelineLength {
  const lastEnd = Math.max(0, input.lastClipEnd || 0);
  const content = Math.max(0, input.contentDuration || 0);
  if (input.flex) {
    return {
      boundary: Math.max(FLEX_MIN_DISPLAY_SEC, snap01(lastEnd + FLEX_TAIL_SEC)),
      dragCap: Math.max(FLEX_MIN_DRAG_SEC, content * 2),
      exportLength: lastEnd,
      flex: true,
    };
  }
  const boundary = getAssetTimelineDuration(
    input.assetIdx,
    input.videoDurations,
    input.targetDuration,
    input.customDurations,
    input.bedDuration,
  );
  return {
    boundary,
    dragCap: boundary * 2,
    exportLength: Math.max(boundary, lastEnd),
    flex: false,
  };
}

/** Ende des am weitesten rechts liegenden favorisierten TTS-Chips. */
export function lastFavClipEnd(blocks: Block[]): number {
  let lastEnd = 0;
  for (const b of blocks) {
    if (b.favIdx < 0) continue;
    const take = b.takes[b.favIdx];
    if (!take?.duration) continue;
    const end = (b.offset ?? 0) + take.duration;
    if (end > lastEnd) lastEnd = end;
  }
  return lastEnd;
}

/** Summe der Dauern aller favorisierten TTS-Chips (0 ohne Chips). */
export function favClipDurationSum(blocks: Block[]): number {
  let sum = 0;
  for (const b of blocks) {
    if (b.favIdx < 0) continue;
    const take = b.takes[b.favIdx];
    if (!take?.duration) continue;
    sum += take.duration;
  }
  return sum;
}

/** Ende des am weitesten rechts liegenden STS-MasterClips (getrimmt). */
export function lastMasterClipEnd(clips: MasterClip[]): number {
  let lastEnd = 0;
  for (const c of clips) {
    const end = c.offset + Math.max(0, c.outPoint - c.inPoint);
    if (end > lastEnd) lastEnd = end;
  }
  return lastEnd;
}

/** Summe der getrimmten Längen aller STS-MasterClips (0 ohne Clips). */
export function masterClipDurationSum(clips: MasterClip[]): number {
  let sum = 0;
  for (const c of clips) {
    sum += Math.max(0, c.outPoint - c.inPoint);
  }
  return sum;
}

/**
 * Letztes Clip-Ende für `resolveTimelineLength.lastClipEnd`, TTS- und
 * STS-Assets gemeinsam (Spec 2026-09-21, F2). STS-Assets nutzen die
 * MasterClips (getrimmt), TTS die favorisierten Blocks — `lastFavClipEnd`
 * liefert für STS-Blocks immer 0 (kein `favIdx`/`takes`-Konzept dort), das
 * ließ den Multi-Lingual-STS-Badge ohne Video auf die Voice-Summe zurückfallen
 * statt die gerenderte Länge zu zeigen. Gleiche Dispatch-Regel wie
 * `exportSnapshot.lastVoiceClipEnd` (`isStsMode`) — keine zweite Regel.
 */
export function lastClipEndForAsset(isStsActive: boolean, blocks: Block[], masterClips: MasterClip[]): number {
  return isStsActive ? lastMasterClipEnd(masterClips) : lastFavClipEnd(blocks);
}
